import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, Bot, Rocket, Shield } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import DeployWizard from "../components/DeployWizard";
import MyAgentsDashboard from "../components/MyAgentsDashboard";
import { useToast } from "../hooks/useToast";

export default function DeployPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [view, setView] = useState("wizard"); // "wizard" | "agents"
  const [deployedAgent, setDeployedAgent] = useState(null);

  const urlParams = new URLSearchParams(window.location.search);
  const agentFromUrl = urlParams.get("agent");

  return (
    <>
      <SEOHead
        title={t("deploy.page_title", "Deploy to Cloud - Max Employee")}
        description={t("deploy.page_desc", "Deploy your AI agent to production in minutes.")}
      />
      <Header />
      <main className="min-h-screen pt-20 pb-12 px-3 sm:px-4">
        <div className="max-w-5xl mx-auto">
          {/* Top Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="flex items-center gap-2 font-mono text-xs text-zinc-500 hover:text-amber-400 transition-colors uppercase tracking-wider"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("deploy.back_to_dashboard", "Back to Dashboard")}
              </Link>
            </div>
            <div className="flex items-center gap-2 flex-1 sm:flex-none w-full sm:w-auto">
              <button
                onClick={() => setView("wizard")}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors ${
                  view === "wizard"
                    ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                <Rocket className="w-3.5 h-3.5" />
                {t("deploy.deploy_wizard", "Deploy Wizard")}
              </button>
              <button
                onClick={() => {
                  setView("agents");
                  addToast({
                    type: "info",
                    title: t("deploy.my_agents", "My Agents"),
                    description: t("deploy.viewing_agents", "Viewing your deployed agents."),
                  });
                }}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-colors ${
                  view === "agents"
                    ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                <Bot className="w-3.5 h-3.5" />
                {t("deploy.my_agents", "My Agents")}
              </button>
            </div>
          </div>

          {/* Trust Bar */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-8 py-3 border border-zinc-800 bg-zinc-950/20">
            <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5 text-emerald-400" />
              {t("deploy.trust_1", "7-day free trial")}
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5 text-emerald-400" />
              {t("deploy.trust_2", "Cancel anytime")}
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5 text-emerald-400" />
              {t("deploy.trust_3", "No lock-in contracts")}
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500 uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5 text-emerald-400" />
              {t("deploy.trust_4", "SOC 2 compliant")}
            </div>
          </div>

          {/* Content */}
          {view === "wizard" ? (
            <DeployWizard initialAgent={agentFromUrl ? { id: agentFromUrl } : null} />
          ) : (
            <MyAgentsDashboard onDeployNew={() => setView("wizard")} />
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
